import React, { useState } from "react";
import { CheckCircle, XCircle, MessageSquare, Loader2 } from "lucide-react";

/**
 * ApprovalActions
 * Bottom action bar with Approve/Reject buttons, rejection reason, and optional notes.
 */
const ApprovalActions = ({
  record,
  theme,
  isRTL,
  t,
  onApprove,
  onReject,
  onActionComplete,
}) => {
  const isDark = theme === "dark";
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(null); // "approve" | "reject" | null
  const [showConfirm, setShowConfirm] = useState(null); // "approve" | null
  const [error, setError] = useState("");

  const approvalStatus = record.overallStatus || "pending";
  const isFullyApproved = approvalStatus === "approved";
  const isRejected = approvalStatus === "rejected";

  const handleApproveClick = () => {
    setShowRejectForm(false);
    setShowConfirm("approve");
  };

  const handleConfirmApprove = async () => {
    setLoading("approve");
    setError("");
    try {
      await onApprove(record);
      onActionComplete?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(null);
      setShowConfirm(null);
    }
  };

  const handleRejectClick = () => {
    setShowConfirm(null);
    setShowRejectForm(true);
  };

  const handleConfirmReject = async () => {
    if (!rejectionReason.trim()) {
      setError(t.reasonRequired);
      return;
    }
    setLoading("reject");
    setError("");
    try {
      await onReject(record, rejectionReason.trim());
      onActionComplete?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(null);
      setShowRejectForm(false);
      setRejectionReason("");
    }
  };

  if (isFullyApproved || isRejected) {
    return null;
  }

  const isWorkflowActive = Boolean(record.workflowInstanceId);
  const cannotActOnWorkflow = isWorkflowActive && !record.currentUserCanAct;

  if (cannotActOnWorkflow) {
    return (
      <div
        style={{
          borderTop: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"}`,
          padding: "20px 0 0",
          direction: isRTL ? "rtl" : "ltr",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            padding: "16px",
            borderRadius: "12px",
            background: isDark ? "rgba(245, 158, 11, 0.1)" : "rgba(254, 243, 199, 0.5)",
            border: `1.5px solid ${isDark ? "rgba(245, 158, 11, 0.25)" : "rgba(245, 158, 11, 0.35)"}`,
            color: isDark ? "#fcd34d" : "#b45309",
            fontFamily: "'Cairo', sans-serif",
          }}
        >
          <span style={{ fontSize: "18px" }}>⚠️</span>
          <div>
            <div style={{ fontWeight: 700, fontSize: "14px" }}>
              {isRTL ? "في انتظار دور موافقة آخر" : "Waiting for another approval step"}
            </div>
            <div style={{ fontSize: "12px", opacity: 0.85, marginTop: "4px" }}>
              {isRTL 
                ? "هذا الطلب خاضع لنظام سير العمل المخصص. ليس لديك صلاحية اتخاذ إجراء في هذه المرحلة حالياً." 
                : "This request is controlled by a custom workflow. You do not have permission to act on this step."}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        borderTop: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"}`,
        padding: "20px 0 0",
        direction: isRTL ? "rtl" : "ltr",
      }}
    >
      {/* Error message */}
      {error && (
        <div
          style={{
            padding: "10px 14px",
            borderRadius: "10px",
            background: isDark ? "rgba(239,68,68,0.12)" : "rgba(254,226,226,0.8)",
            border: `1px solid ${isDark ? "rgba(239,68,68,0.25)" : "rgba(239,68,68,0.3)"}`,
            color: isDark ? "#fca5a5" : "#991b1b",
            fontSize: "13px",
            fontWeight: 500,
            fontFamily: "'Cairo', sans-serif",
            marginBottom: "14px",
          }}
        >
          {error}
        </div>
      )}

      {/* Confirm approval dialog */}
      {showConfirm === "approve" && (
        <div
          style={{
            padding: "16px",
            borderRadius: "14px",
            background: isDark ? "rgba(16,185,129,0.08)" : "rgba(209,250,229,0.6)",
            border: `1px solid ${isDark ? "rgba(16,185,129,0.2)" : "rgba(16,185,129,0.25)"}`,
            marginBottom: "16px",
          }}
        >
          <p
            style={{
              margin: "0 0 12px",
              fontSize: "13px",
              fontWeight: 500,
              color: isDark ? "#a7f3d0" : "#065f46",
              fontFamily: "'Cairo', sans-serif",
            }}
          >
            {t.confirmApproval}
          </p>
          <div style={{ display: "flex", gap: "10px", flexDirection: isRTL ? "row-reverse" : "row" }}>
            <button
              onClick={handleConfirmApprove}
              disabled={loading === "approve"}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                padding: "8px 20px",
                borderRadius: "10px",
                border: "none",
                background: "linear-gradient(135deg, #10b981, #059669)",
                color: "#fff",
                fontSize: "13px",
                fontWeight: 600,
                cursor: loading === "approve" ? "not-allowed" : "pointer",
                opacity: loading === "approve" ? 0.7 : 1,
                fontFamily: "'Cairo', sans-serif",
                transition: "all 0.2s ease",
              }}
            >
              {loading === "approve" ? (
                <Loader2 size={14} style={{ animation: "ot-spin 0.7s linear infinite" }} />
              ) : (
                <CheckCircle size={14} />
              )}
              {loading === "approve" ? t.approving : t.approve}
            </button>
            <button
              onClick={() => setShowConfirm(null)}
              disabled={loading === "approve"}
              style={{
                padding: "8px 16px",
                borderRadius: "10px",
                border: `1px solid ${isDark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.1)"}`,
                background: "transparent",
                color: isDark ? "#94a3b8" : "#64748b",
                fontSize: "13px",
                fontWeight: 500,
                cursor: "pointer",
                fontFamily: "'Cairo', sans-serif",
              }}
            >
              {t.clearAll}
            </button>
          </div>
        </div>
      )}

      {/* Reject form */}
      {showRejectForm && (
        <div
          style={{
            padding: "16px",
            borderRadius: "14px",
            background: isDark ? "rgba(239,68,68,0.08)" : "rgba(254,226,226,0.4)",
            border: `1px solid ${isDark ? "rgba(239,68,68,0.2)" : "rgba(239,68,68,0.2)"}`,
            marginBottom: "16px",
          }}
        >
          <p
            style={{
              margin: "0 0 10px",
              fontSize: "13px",
              fontWeight: 600,
              color: isDark ? "#fca5a5" : "#991b1b",
              fontFamily: "'Cairo', sans-serif",
            }}
          >
            {t.rejectionReason} *
          </p>
          <textarea
            value={rejectionReason}
            onChange={(e) => { setRejectionReason(e.target.value); setError(""); }}
            placeholder={t.rejectionReasonPlaceholder}
            rows={3}
            style={{
              width: "100%",
              padding: "10px 14px",
              borderRadius: "10px",
              border: `1px solid ${isDark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.1)"}`,
              background: isDark ? "rgba(0,0,0,0.3)" : "rgba(255,255,255,0.9)",
              color: isDark ? "#f1f5f9" : "#1e293b",
              fontSize: "13px",
              fontFamily: "'Cairo', sans-serif",
              resize: "vertical",
              outline: "none",
              boxSizing: "border-box",
              direction: isRTL ? "rtl" : "ltr",
            }}
          />
          <div
            style={{
              display: "flex",
              gap: "10px",
              marginTop: "12px",
              flexDirection: isRTL ? "row-reverse" : "row",
            }}
          >
            <button
              onClick={handleConfirmReject}
              disabled={loading === "reject"}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                padding: "8px 20px",
                borderRadius: "10px",
                border: "none",
                background: "linear-gradient(135deg, #ef4444, #dc2626)",
                color: "#fff",
                fontSize: "13px",
                fontWeight: 600,
                cursor: loading === "reject" ? "not-allowed" : "pointer",
                opacity: loading === "reject" ? 0.7 : 1,
                fontFamily: "'Cairo', sans-serif",
                transition: "all 0.2s ease",
              }}
            >
              {loading === "reject" ? (
                <Loader2 size={14} style={{ animation: "ot-spin 0.7s linear infinite" }} />
              ) : (
                <XCircle size={14} />
              )}
              {loading === "reject" ? t.rejecting : t.reject}
            </button>
            <button
              onClick={() => { setShowRejectForm(false); setRejectionReason(""); setError(""); }}
              disabled={loading === "reject"}
              style={{
                padding: "8px 16px",
                borderRadius: "10px",
                border: `1px solid ${isDark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.1)"}`,
                background: "transparent",
                color: isDark ? "#94a3b8" : "#64748b",
                fontSize: "13px",
                fontWeight: 500,
                cursor: "pointer",
                fontFamily: "'Cairo', sans-serif",
              }}
            >
              {t.clearAll}
            </button>
          </div>
        </div>
      )}

      {/* Main action buttons */}
      {!showConfirm && !showRejectForm && (
        <>
          {/* Optional notes */}
          <div style={{ marginBottom: "16px" }}>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                fontSize: "12px",
                fontWeight: 600,
                color: isDark ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.5)",
                fontFamily: "'Cairo', sans-serif",
                marginBottom: "6px",
                flexDirection: isRTL ? "row-reverse" : "row",
              }}
            >
              <MessageSquare size={12} />
              {t.addNotes}
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t.notesPlaceholder}
              rows={2}
              style={{
                width: "100%",
                padding: "10px 14px",
                borderRadius: "10px",
                border: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.08)"}`,
                background: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.02)",
                color: isDark ? "#f1f5f9" : "#1e293b",
                fontSize: "13px",
                fontFamily: "'Cairo', sans-serif",
                resize: "vertical",
                outline: "none",
                boxSizing: "border-box",
                direction: isRTL ? "rtl" : "ltr",
                transition: "border-color 0.2s ease",
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = isDark ? "rgba(99,102,241,0.5)" : "rgba(99,102,241,0.4)";
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.08)";
              }}
            />
          </div>

          <div
            style={{
              display: "flex",
              gap: "12px",
              flexDirection: isRTL ? "row-reverse" : "row",
            }}
          >
            {/* Approve */}
            <button
              onClick={handleApproveClick}
              style={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
                padding: "12px 24px",
                borderRadius: "12px",
                border: "none",
                background: "linear-gradient(135deg, #10b981, #059669)",
                color: "#fff",
                fontSize: "14px",
                fontWeight: 700,
                cursor: "pointer",
                fontFamily: "'Cairo', sans-serif",
                transition: "all 0.2s ease",
                boxShadow: "0 4px 14px rgba(16, 185, 129, 0.3)",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-1px)";
                e.currentTarget.style.boxShadow = "0 6px 20px rgba(16, 185, 129, 0.4)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "0 4px 14px rgba(16, 185, 129, 0.3)";
              }}
            >
              <CheckCircle size={16} />
              {t.approve}
            </button>

            {/* Reject */}
            <button
              onClick={handleRejectClick}
              style={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
                padding: "12px 24px",
                borderRadius: "12px",
                border: `1.5px solid ${isDark ? "rgba(239,68,68,0.4)" : "rgba(239,68,68,0.3)"}`,
                background: isDark ? "rgba(239,68,68,0.1)" : "rgba(254,226,226,0.5)",
                color: isDark ? "#fca5a5" : "#dc2626",
                fontSize: "14px",
                fontWeight: 700,
                cursor: "pointer",
                fontFamily: "'Cairo', sans-serif",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-1px)";
                e.currentTarget.style.background = isDark ? "rgba(239,68,68,0.18)" : "rgba(254,226,226,0.8)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.background = isDark ? "rgba(239,68,68,0.1)" : "rgba(254,226,226,0.5)";
              }}
            >
              <XCircle size={16} />
              {t.reject}
            </button>
          </div>
        </>
      )}

      <style>{`
        @keyframes ot-spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default ApprovalActions;
